import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@core/auth/auth.service';
import { ShoppingBasket, ShoppingGroupDetails } from '@core/models/models';

import { environment } from '../../../../../environments/environment';
import { shouldOpenWhatsAppInPlace, whatsAppShareUrl } from './whatsapp-share-url';

@Component({
  selector: 'app-group-share-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './group-share-modal.component.html',
  styleUrl: './group-share-modal.component.css'
})
export class GroupShareModalComponent implements OnChanges {
  readonly auth = inject(AuthService);

  @Input() isOpen = false;
  @Input() basket: ShoppingBasket | null = null;
  @Input() selectedCity = '';
  @Input() selectedBranchId: number | null = null;
  @Input() shareTitle = 'שיתוף סל הקניות';
  @Input() whatsappIntro = 'היי! שיתפתי איתך סל קניות ב-Commpare:';
  @Input() linkPage: 'cart' | 'store-list' = 'cart';

  @Output() close = new EventEmitter<void>();
  @Output() basketUpdated = new EventEmitter<ShoppingBasket>();
  @Output() statusNotice = new EventEmitter<string>();

  shareChannel: 'select' | 'gmail' | 'whatsapp' = 'select';
  gmailUsername = '';
  whatsappLocalPhone = '';
  shareSubmitting = false;
  shareSuccessMsg: string | null = null;
  shareErrorMsg: string | null = null;
  unlinkingPersonal = false;

  groupDetails: ShoppingGroupDetails | null = null;
  loadingGroupDetails = false;
  removingMemberUserId: number | null = null;
  whatsAppInviteLink: string | null = null;

  private whatsAppInviteSeq = 0;
  private whatsAppInvitePromise: Promise<string> | null = null;

  get fullGmailAddress(): string {
    const user = this.gmailUsername.trim().toLowerCase().replace(/@.*$/, '');
    return user ? `${user}@gmail.com` : '';
  }

  get fullWhatsAppPhone(): string {
    const digits = this.whatsappLocalPhone.replace(/\D/g, '');
    const local = digits.startsWith('0') ? digits.slice(1) : digits;
    return local ? `+972${local}` : '';
  }

  get isGmailValid(): boolean {
    const user = this.gmailUsername.trim().toLowerCase().replace(/@.*$/, '');
    return /^[a-zA-Z0-9._%+-]{2,}$/.test(user);
  }

  get isWhatsAppValid(): boolean {
    const digits = this.whatsappLocalPhone.replace(/\D/g, '');
    const local = digits.startsWith('0') ? digits.slice(1) : digits;
    return /^[5]\d{8}$/.test(local);
  }

  selectChannel(channel: 'select' | 'gmail' | 'whatsapp'): void {
    this.shareChannel = channel;
    this.shareErrorMsg = null;
    this.shareSuccessMsg = null;
    if (channel === 'whatsapp') {
      void this.ensureWhatsAppInviteLink();
    }
  }

  closeModal(): void {
    this.shareChannel = 'select';
    this.shareErrorMsg = null;
    this.shareSuccessMsg = null;
    this.gmailUsername = '';
    this.whatsappLocalPhone = '';
    this.whatsAppInviteSeq++;
    this.whatsAppInviteLink = null;
    this.whatsAppInvitePromise = null;
    this.close.emit();
  }

  async submitGmailShare(): Promise<void> {
    if (!this.basket || !this.isGmailValid) return;
    this.shareSubmitting = true;
    this.shareErrorMsg = null;
    try {
      const email = this.fullGmailAddress;
      const groupId = this.basket.groupId || this.auth.currentUser?.defaultGroupId;
      if (groupId) {
        if (!this.basket.groupId) {
          try {
            const updated = await firstValueFrom(this.auth.setBasketGroup(this.basket.id, groupId));
            this.basket = updated;
            this.basketUpdated.emit(updated);
          } catch (linkErr) {
            console.warn('Could not associate basket with group before sharing:', linkErr);
          }
        }
        const page = this.linkPage === 'store-list' ? 'store-list' : null;
        await firstValueFrom(this.auth.inviteMember(groupId, email, 'editor', this.basket.id, page));
      }
      this.shareSuccessMsg = `נשלחה הזמנה ל-${email}`;
      const sharedNotice = this.linkPage === 'store-list'
        ? `הרשימה שותפה בהצלחה עם ${email}`
        : `הסל שותף בהצלחה עם ${email}`;
      this.statusNotice.emit(sharedNotice);
      setTimeout(() => this.closeModal(), 1800);
    } catch (err: any) {
      this.shareErrorMsg =
        err?.error?.error ||
        err?.error?.message ||
        (typeof err?.error === 'string' ? err.error : null) ||
        'שגיאה בשליחת הזמנה';
    } finally {
      this.shareSubmitting = false;
    }
  }

  submitWhatsAppShare(): void {
    if (!this.basket || !this.isWhatsAppValid || this.shareSubmitting) return;
    const inviteLink = this.whatsAppInviteLink;
    if (!inviteLink) {
      void this.ensureWhatsAppInviteLink();
      return;
    }
    this.shareErrorMsg = null;
    const phone = this.fullWhatsAppPhone.replace('+', '');
    const message = `${this.whatsappIntro}\n${inviteLink}`;
    const waUrl = whatsAppShareUrl(phone, message, navigator.userAgent);
    const opened = shouldOpenWhatsAppInPlace(navigator.userAgent)
      ? this.openWhatsAppInPlace(waUrl)
      : this.openWhatsAppPopup(waUrl);
    if (!opened) {
      this.shareErrorMsg = 'הדפדפן חסם את פתיחת WhatsApp. אשרו חלונות קופצים ונסו שוב.';
      return;
    }
    this.shareSuccessMsg = 'וואטסאפ נפתח עם ההזמנה. לחצו שליחה בצ׳אט.';
    this.statusNotice.emit(this.linkPage === 'store-list' ? 'הרשימה שותפה ל-WhatsApp' : 'הסל שותף ל-WhatsApp');
    setTimeout(() => this.closeModal(), 1800);
  }

  private openWhatsAppInPlace(url: string): boolean {
    window.location.assign(url);
    return true;
  }

  private openWhatsAppPopup(url: string): boolean {
    const popup = window.open(url, '_blank');
    return !!popup;
  }

  private ensureWhatsAppInviteLink(): Promise<string> {
    if (this.whatsAppInviteLink) return Promise.resolve(this.whatsAppInviteLink);
    if (this.whatsAppInvitePromise) return this.whatsAppInvitePromise;
    const seq = ++this.whatsAppInviteSeq;
    this.whatsAppInvitePromise = this.buildWhatsAppInviteLink()
      .then((url) => {
        if (seq === this.whatsAppInviteSeq) this.whatsAppInviteLink = url;
        return url;
      })
      .catch((err: any) => {
        if (seq === this.whatsAppInviteSeq) {
          this.shareErrorMsg =
            err?.error?.error ||
            err?.error?.message ||
            (typeof err?.error === 'string' ? err.error : null) ||
            'שגיאה ביצירת קישור WhatsApp';
        }
        throw err;
      })
      .finally(() => {
        if (seq === this.whatsAppInviteSeq) this.whatsAppInvitePromise = null;
      });
    return this.whatsAppInvitePromise;
  }

  private async buildWhatsAppInviteLink(): Promise<string> {
    if (!this.basket) throw new Error('missing basket');
    let base = (this.auth.publicBaseUrl || '').trim();
    if (!base) {
      try {
        const cfg = await this.auth.getAuthConfig();
        if (cfg?.publicBaseUrl) base = cfg.publicBaseUrl.trim();
      } catch {
        // ignore network failure
      }
    }
    if (!base && (environment as { publicBaseUrl?: string }).publicBaseUrl) {
      base = (environment as { publicBaseUrl?: string }).publicBaseUrl!.trim();
    }
    const inviteUrl = (base || window.location.origin || '').replace(/\/+$/, '');
    const groupId = this.basket.groupId || this.auth.currentUser?.defaultGroupId;
    if (groupId && !this.basket.groupId) {
      try {
        const updated = await firstValueFrom(this.auth.setBasketGroup(this.basket.id, groupId));
        this.basket = updated;
        this.basketUpdated.emit(updated);
      } catch (linkErr) {
        console.warn('Could not associate basket with group before sharing:', linkErr);
      }
    }
    let tokenParam = '';
    if (groupId) {
      try {
        const res = await firstValueFrom(this.auth.getGroupInviteToken(groupId));
        if (res?.token) tokenParam = `joinToken=${encodeURIComponent(res.token)}&`;
      } catch (tokenErr) {
        console.warn('Could not generate group invite token:', tokenErr);
      }
    }
    const pageParam = this.linkPage === 'store-list' ? 'login=1&page=store-list' : 'tab=cart';
    return `${inviteUrl}/?${tokenParam}basketId=${this.basket.id}&${pageParam}&_t=${Date.now()}`;
  }

  unlinkPersonalBasket(): void {
    this.closeModal();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const basketChange = changes['basket'];
    if (basketChange && basketChange.previousValue?.id !== basketChange.currentValue?.id) {
      this.whatsAppInviteSeq++;
      this.whatsAppInviteLink = null;
      this.whatsAppInvitePromise = null;
      if (this.shareChannel === 'whatsapp' && this.isOpen) {
        void this.ensureWhatsAppInviteLink();
      }
    }
    if ((changes['isOpen'] && this.isOpen) || (changes['basket'] && this.basket)) {
      this.loadGroupDetails();
    }
  }

  loadGroupDetails(): void {
    const groupId = this.basket?.groupId || this.auth.currentUser?.defaultGroupId;
    if (!groupId) {
      this.groupDetails = null;
      return;
    }
    this.loadingGroupDetails = true;
    this.auth.getGroup(groupId).subscribe({
      next: (details) => {
        this.groupDetails = details;
        this.loadingGroupDetails = false;
      },
      error: (err) => {
        console.warn('Could not load group details:', err);
        this.loadingGroupDetails = false;
      }
    });
  }

  removeMember(targetUserId: number): void {
    if (!this.groupDetails) return;
    this.removingMemberUserId = targetUserId;
    this.auth.removeMember(this.groupDetails.id, targetUserId).subscribe({
      next: () => {
        this.removingMemberUserId = null;
        this.statusNotice.emit('החבר הוסר מהקבוצה.');
        this.loadGroupDetails();
      },
      error: (err) => {
        this.removingMemberUserId = null;
        this.shareErrorMsg = err?.error?.error || 'שגיאה בהסרת חבר';
      }
    });
  }
}
